import {
  ARROW_HEAD_SIZE,
  BLOCK_FRAME_PADDING,
  BLOCK_LABEL_HEIGHT,
  BLOCK_PADDING_Y,
  BOTTOM_PADDING,
  FIRST_MESSAGE_GAP,
  HEADER_HEIGHT,
  HEADER_MIN_WIDTH,
  HEADER_PADDING_X,
  LANE_GAP_PADDING,
  LIFELINE_TAIL,
  MESSAGE_FONT_SIZE,
  MESSAGE_ROW_HEIGHT,
  MIN_LANE_GAP,
  NOTE_MIN_WIDTH,
  NOTE_PADDING_X,
  NOTE_ROW_HEIGHT,
  NOTE_SIDE_OFFSET,
  NOTE_VERTICAL_INSET,
  SIDE_PADDING,
  TITLE_FONT_SIZE,
  TITLE_GAP,
  TOP_PADDING,
  estimateTextWidth,
} from './sequence-constants';
import {
  SequenceArrowKind,
  SequenceEventKind,
  SequenceNotePlacement,
  type MermaidSequence,
  type MermaidSequenceEvent,
} from './sequence-types';

/** Discriminator for laid-out render primitives. */
export enum SequenceLayoutKind {
  Message = 'message',
  Note = 'note',
  Block = 'block',
}

/** A participant lane with its resolved horizontal position. */
export interface SequenceLaneLayout {
  id: string;
  label: string;
  isActor: boolean;
  /** X of the lane center (lifeline). */
  centerX: number;
  /** Width of the header box (centered on centerX). */
  headerWidth: number;
}

export interface SequenceMessageLayout {
  type: SequenceLayoutKind.Message;
  text: string;
  arrow: SequenceArrowKind;
  fromId: string;
  fromX: number;
  toId: string;
  toX: number;
  /** Y of the message arrow line. */
  y: number;
  /** True when from === to (self-message, rendered as a loop). */
  selfCall: boolean;
}

export interface SequenceNoteLayout {
  type: SequenceLayoutKind.Note;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SequenceBlockLayout {
  type: SequenceLayoutKind.Block;
  label: string;
  blockType: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SequenceLayout {
  width: number;
  height: number;
  lifelineTop: number;
  lifelineBottom: number;
  titleY: number | null;
  title?: string;
  lanes: SequenceLaneLayout[];
  messages: SequenceMessageLayout[];
  notes: SequenceNoteLayout[];
  blocks: SequenceBlockLayout[];
}

interface SequenceLaneBuildResult {
  lanes: SequenceLaneLayout[];
  indexById: Map<string, number>;
}

interface SequenceEventLayoutResult {
  messages: SequenceMessageLayout[];
  notes: SequenceNoteLayout[];
  blocks: SequenceBlockLayout[];
  y: number;
}

interface SequenceHorizontalBounds {
  minX: number;
  maxX: number;
}

function headerWidthFor(label: string): number {
  return Math.max(HEADER_MIN_WIDTH, estimateTextWidth(label) + HEADER_PADDING_X * 2);
}

function buildSequenceLanes(
  participants: MermaidSequence['participants'],
): SequenceLaneBuildResult {
  const lanes: SequenceLaneLayout[] = [];
  const indexById = new Map<string, number>();
  const headerWidths = participants.map((participant) => headerWidthFor(participant.label));

  let cursor = SIDE_PADDING;
  participants.forEach((participant, index) => {
    const headerWidth = headerWidths[index];
    if (index === 0) {
      cursor += headerWidth / 2;
    } else {
      const previousHeaderWidth = headerWidths[index - 1];
      const needed =
        previousHeaderWidth / 2 + headerWidth / 2 + LANE_GAP_PADDING;
      cursor += Math.max(MIN_LANE_GAP, needed);
    }
    lanes.push({
      id: participant.id,
      label: participant.label,
      isActor: participant.isActor,
      centerX: cursor,
      headerWidth,
    });
    indexById.set(participant.id, index);
  });

  return { lanes, indexById };
}

function getLaneCenter(input: {
  id: string;
  indexById: Map<string, number>;
  lanes: SequenceLaneLayout[];
}): number {
  const index = input.indexById.get(input.id);
  if (index == null) {
    return input.lanes.length > 0 ? input.lanes[0].centerX : SIDE_PADDING;
  }

  return input.lanes[index].centerX;
}

function getSequenceTitleY(model: MermaidSequence): number | null {
  return model.title ? TOP_PADDING + TITLE_FONT_SIZE : null;
}

function getSequenceHeaderTop(titleY: number | null): number {
  return titleY != null ? titleY + TITLE_GAP : TOP_PADDING;
}

function layoutSequenceEvents(input: {
  events: MermaidSequenceEvent[];
  centerOf: (id: string) => number;
  lanes: SequenceLaneLayout[];
  startY: number;
}): SequenceEventLayoutResult {
  const messages: SequenceMessageLayout[] = [];
  const notes: SequenceNoteLayout[] = [];
  const blocks: SequenceBlockLayout[] = [];
  let y = input.startY;

  const walk = (events: MermaidSequenceEvent[]): void => {
    events.forEach((event) => {
      if (event.kind === SequenceEventKind.Message) {
        const fromX = input.centerOf(event.from);
        const toX = input.centerOf(event.to);
        messages.push({
          type: SequenceLayoutKind.Message,
          text: event.text,
          arrow: event.arrow,
          fromId: event.from,
          fromX,
          toId: event.to,
          toX,
          y: y + MESSAGE_ROW_HEIGHT / 2,
          selfCall: event.from === event.to,
        });
        y += MESSAGE_ROW_HEIGHT;
        return;
      }

      if (event.kind === SequenceEventKind.Note) {
        const note = layoutNote(event, input.centerOf, y);
        notes.push(note);
        y += NOTE_ROW_HEIGHT;
        return;
      }

      const top = y;
      y += BLOCK_LABEL_HEIGHT + BLOCK_PADDING_Y;
      walk(event.events);
      y += BLOCK_PADDING_Y;
      const spanned = spanForEvents(event.events, input.centerOf, input.lanes);
      blocks.push({
        type: SequenceLayoutKind.Block,
        label: event.label,
        blockType: event.type,
        x: spanned.minX,
        y: top,
        width: spanned.maxX - spanned.minX,
        height: y - top,
      });
    });
  };

  walk(input.events);

  return { messages, notes, blocks, y };
}

function includeSequenceHorizontalEdges(input: {
  bounds: SequenceHorizontalBounds;
  left: number;
  right: number;
}): void {
  input.bounds.minX = Math.min(input.bounds.minX, input.left);
  input.bounds.maxX = Math.max(input.bounds.maxX, input.right);
}

function getSequenceHorizontalBounds(input: {
  lanes: SequenceLaneLayout[];
  notes: SequenceNoteLayout[];
  blocks: SequenceBlockLayout[];
}): SequenceHorizontalBounds {
  const bounds: SequenceHorizontalBounds = {
    minX: SIDE_PADDING,
    maxX: SIDE_PADDING,
  };

  input.lanes.forEach((lane) => {
    includeSequenceHorizontalEdges({
      bounds,
      left: lane.centerX - lane.headerWidth / 2,
      right: lane.centerX + lane.headerWidth / 2,
    });
  });
  input.notes.forEach((note) => {
    includeSequenceHorizontalEdges({
      bounds,
      left: note.x,
      right: note.x + note.width,
    });
  });
  input.blocks.forEach((block) => {
    includeSequenceHorizontalEdges({
      bounds,
      left: block.x,
      right: block.x + block.width,
    });
  });

  return bounds;
}

function applySequenceHorizontalShift(input: {
  shift: number;
  lanes: SequenceLaneLayout[];
  messages: SequenceMessageLayout[];
  notes: SequenceNoteLayout[];
  blocks: SequenceBlockLayout[];
}): void {
  if (input.shift === 0) {
    return;
  }

  input.lanes.forEach((lane) => {
    lane.centerX += input.shift;
  });
  input.messages.forEach((message) => {
    message.fromX += input.shift;
    message.toX += input.shift;
  });
  input.notes.forEach((note) => {
    note.x += input.shift;
  });
  input.blocks.forEach((block) => {
    block.x += input.shift;
  });
}

/**
 * Pure layout: positions lanes left-to-right and stacks events top-to-bottom.
 * No DOM measurement — text widths are estimated, matching the sibling
 * renderers. Self-messages and unknown arrows still get a valid line.
 */
export function layoutSequence(model: MermaidSequence): SequenceLayout {
  const { lanes, indexById } = buildSequenceLanes(model.participants);
  const centerOf = (id: string): number =>
    getLaneCenter({ id, indexById, lanes });
  const titleY = getSequenceTitleY(model);
  const headerTop = getSequenceHeaderTop(titleY);
  const lifelineTop = headerTop + HEADER_HEIGHT;
  const { messages, notes, blocks, y } = layoutSequenceEvents({
    events: model.events,
    centerOf,
    lanes,
    startY: lifelineTop + FIRST_MESSAGE_GAP,
  });
  const lifelineBottom = y + LIFELINE_TAIL;
  const horizontalBounds = getSequenceHorizontalBounds({ lanes, notes, blocks });
  const shift = SIDE_PADDING - horizontalBounds.minX;
  applySequenceHorizontalShift({ shift, lanes, messages, notes, blocks });
  const width = horizontalBounds.maxX + shift + SIDE_PADDING;
  const height = lifelineBottom + BOTTOM_PADDING;

  return {
    width,
    height,
    lifelineTop,
    lifelineBottom,
    titleY,
    title: model.title,
    lanes,
    messages,
    notes,
    blocks,
  };
}

function layoutNote(
  note: { participants: string[]; placement: SequenceNotePlacement; text: string },
  centerOf: (id: string) => number,
  y: number,
): SequenceNoteLayout {
  const textWidth = estimateTextWidth(note.text, MESSAGE_FONT_SIZE) + NOTE_PADDING_X * 2;
  const width = Math.max(NOTE_MIN_WIDTH, textWidth);
  const height = NOTE_ROW_HEIGHT - NOTE_VERTICAL_INSET;

  if (note.placement === SequenceNotePlacement.Over) {
    const xs = note.participants.map(centerOf);
    const mid = xs.reduce((a, b) => a + b, 0) / xs.length;
    return { type: SequenceLayoutKind.Note, text: note.text, x: mid - width / 2, y, width, height };
  }
  const anchor = centerOf(note.participants[0]);
  const x =
    note.placement === SequenceNotePlacement.Left
      ? anchor - width - NOTE_SIDE_OFFSET
      : anchor + NOTE_SIDE_OFFSET;
  return { type: SequenceLayoutKind.Note, text: note.text, x, y, width, height };
}

/** Horizontal span (min/max X) covered by a set of events, for block frames. */
function spanForEvents(
  events: MermaidSequenceEvent[],
  centerOf: (id: string) => number,
  lanes: SequenceLaneLayout[],
): { minX: number; maxX: number } {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  const include = (x: number) => {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
  };
  const visit = (evs: MermaidSequenceEvent[]) => {
    for (const e of evs) {
      if (e.kind === SequenceEventKind.Message) {
        include(centerOf(e.from));
        include(centerOf(e.to));
      } else if (e.kind === SequenceEventKind.Note) {
        e.participants.forEach((p) => include(centerOf(p)));
      } else {
        visit(e.events);
      }
    }
  };
  visit(events);
  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
    // Empty block — frame around all lanes as a fallback.
    const allX = lanes.map((l) => l.centerX);
    minX = allX.length > 0 ? Math.min(...allX) : SIDE_PADDING;
    maxX = allX.length > 0 ? Math.max(...allX) : SIDE_PADDING + HEADER_MIN_WIDTH;
  }
  const pad = BLOCK_FRAME_PADDING + ARROW_HEAD_SIZE;
  return { minX: minX - pad, maxX: maxX + pad };
}
